// src/pages/Buyer/Invoices.jsx
// ✅ COMPLETE — never goes blank, catches all errors gracefully

import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Button, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Avatar, Tooltip, LinearProgress,
  Tabs, Tab, Alert, Divider,
} from '@mui/material';
import {
  Search, Refresh, Visibility, Receipt, Payment, PictureAsPdf,
  Schedule, CheckCircle, Warning, AttachMoney, Download, ShoppingCart,
} from '@mui/icons-material';
import DialogContentText from '@mui/material/DialogContentText';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { userAPI, enquiryAPI } from '../../services/api';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/helpers';
import toast from 'react-hot-toast';

const getId  = (inv) => inv?.id  || inv?._id  || '';
const getNum = (inv) => inv?.invoiceNumber || inv?.invoice_number || `INV-${getId(inv)}`;
const getAmt = (inv) => inv?.totalAmount   || inv?.total_amount   || 0;

const BuyerInvoices = () => {
  const navigate      = useNavigate();
  const [searchParams]= useSearchParams();

  const [invoices,        setInvoices]        = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [fetchError,      setFetchError]      = useState(null);
  const [searchTerm,      setSearchTerm]      = useState('');
  const [tabValue,        setTabValue]        = useState(0);
  const [selectedInvoice,  setSelectedInvoice]  = useState(null);
  const [viewDialog,       setViewDialog]       = useState(false);
  const [payConfirmDialog, setPayConfirmDialog] = useState(false);
  const [invoiceToPay,     setInvoiceToPay]     = useState(null);

  const TAB_STATUS = ['all', 'pending', 'paid', 'overdue'];

  useEffect(() => {
    const s = searchParams.get('status');
    if (s) { const i = TAB_STATUS.indexOf(s); if (i !== -1) setTabValue(i); }
  }, []);

  useEffect(() => { fetchInvoices(); }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      let data = [];

      // ── Primary: GET /api/user/invoices ──────────────────────
      try {
        const res = await userAPI.getMyInvoices();
        data = Array.isArray(res.data)
          ? res.data
          : (res.data?.invoices || res.data?.data || []);
      } catch (primaryErr) {
        const status = primaryErr?.response?.status;
        console.warn(`userAPI.getMyInvoices → ${status}`, primaryErr.message);

        // ── Fallback: build invoices from enquiries ────────────
        if (status === 404 || status === 405 || status === 500 || !status) {
          const r2   = await enquiryAPI.getUserEnquiries();
          const enqs = Array.isArray(r2.data) ? r2.data : [];
          data = enqs
            .filter(e => e.invoiceNumber || e.invoice_number)
            .map(e => ({
              id:            e.id,
              _id:           e.id,
              invoiceNumber: e.invoiceNumber || e.invoice_number,
              enquiry_id:    e.id,
              product:       e.product || e.productName || 'Fruit Purchase',
              quantity:      e.quantity,
              totalAmount:   e.totalAmount || e.total_amount || 0,
              status:        e.status === 'invoice_sent' ? 'pending'
                           : e.status === 'completed'    ? 'paid'
                           : 'pending',
              issueDate:  e.updatedAt  || e.updated_at  || e.createdAt || e.created_at,
              dueDate:    null,
              paymentTerms: 'Net 7 days',
              notes:      '',
              items:      [],
            }));
        } else {
          throw primaryErr;
        }
      }

      setInvoices(data);
    } catch (err) {
      console.error('fetchInvoices total failure:', err);
      setFetchError('Could not load invoices. Please try refreshing.');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = (inv) => {
    setInvoiceToPay(inv);
    setPayConfirmDialog(true);
  };

  const handleConfirmPay = () => {
    const enquiryId = invoiceToPay?.enquiry_id || getId(invoiceToPay);
    setPayConfirmDialog(false);
    navigate(`/buyer/payment/${enquiryId}`);
  };

  const handleDownload = async (inv) => {
    const id  = getId(inv);
    const num = getNum(inv);
    try {
      const res  = await userAPI.downloadInvoice(id);
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `invoice-${num}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch {
      toast.error('Failed to download invoice');
    }
  };

  const stats = {
    total:         invoices.length,
    pending:       invoices.filter(i => i.status === 'pending').length,
    paid:          invoices.filter(i => i.status === 'paid').length,
    overdue:       invoices.filter(i => i.status === 'overdue').length,
    pendingAmount: invoices.filter(i => ['pending','overdue'].includes(i.status))
                           .reduce((s, i) => s + getAmt(i), 0),
  };

  const statusFilter     = TAB_STATUS[tabValue];
  const filteredInvoices = invoices.filter(inv => {
    const q  = searchTerm.toLowerCase();
    const ok = !q || getNum(inv).toLowerCase().includes(q) ||
               String(inv.enquiry_id || '').includes(q) ||
               (inv.product || '').toLowerCase().includes(q);
    return ok && (statusFilter === 'all' || inv.status === statusFilter);
  });

  if (loading) return (
    <Container sx={{ py: 4 }}>
      <LinearProgress sx={{ mb: 2 }} />
      <Typography color="text.secondary">Loading invoices...</Typography>
    </Container>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>

      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">My Invoices</Typography>
          <Typography variant="body1" color="text.secondary">View and pay your invoices</Typography>
        </Box>
        <Button variant="contained" startIcon={<Refresh />} onClick={fetchInvoices}>Refresh</Button>
      </Box>

      {fetchError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setFetchError(null)}>
          {fetchError}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label:'Total Invoices', value: stats.total,                        color:'#2196f3', icon:<Receipt /> },
          { label:'Pending',        value: stats.pending,                       color:'#ff9800', icon:<Schedule />,    textColor:'warning.main' },
          { label:'Amount Due',     value: formatCurrency(stats.pendingAmount), color:'#f44336', icon:<AttachMoney />, textColor:'error.main', isText:true },
          { label:'Paid',           value: stats.paid,                          color:'#4caf50', icon:<CheckCircle />, textColor:'success.main' },
        ].map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">{s.label}</Typography>
                    <Typography variant="h4" fontWeight="bold"
                      color={s.textColor || 'text.primary'}
                      sx={s.isText ? { fontSize:'1.3rem' } : {}}>
                      {s.value}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor:`${s.color}20`, color:s.color }}>{s.icon}</Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable">
          <Tab label="All"                          icon={<Receipt />}     iconPosition="start" />
          <Tab label={`Pending (${stats.pending})`} icon={<Schedule />}    iconPosition="start" />
          <Tab label={`Paid (${stats.paid})`}       icon={<CheckCircle />} iconPosition="start" />
          <Tab label={`Overdue (${stats.overdue})`} icon={<Warning />}     iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField fullWidth size="small"
          placeholder="Search by invoice number, enquiry ID, or product..."
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{ startAdornment:<InputAdornment position="start"><Search /></InputAdornment> }}
        />
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow:'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor:'grey.50' }}>
              <TableRow>
                {['Invoice #','Enquiry','Product','Amount','Status','Issue Date','Due Date','Actions'].map(h => (
                  <TableCell key={h}>
                    <Typography variant="subtitle2" fontWeight="bold">{h}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Receipt sx={{ fontSize:64, color:'text.disabled', display:'block', mx:'auto', mb:2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {invoices.length === 0 ? 'No invoices yet' : 'No invoices match the filter'}
                    </Typography>
                    {invoices.length === 0 && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Invoices appear once the admin sends you one for a confirmed enquiry.
                        </Typography>
                        <Button variant="outlined" startIcon={<ShoppingCart />}
                          onClick={() => navigate('/buyer/enquiries')}>
                          View My Enquiries
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ) : filteredInvoices.map((inv) => {
                const id  = getId(inv);
                const num = getNum(inv);
                const amt = getAmt(inv);
                return (
                  <TableRow key={id} hover>
                    <TableCell><Typography variant="body2" fontWeight="medium">{num}</Typography></TableCell>
                    <TableCell><Typography variant="body2">#{inv.enquiry_id || id}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{inv.product || inv.productName || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="body2" fontWeight="medium">{formatCurrency(amt)}</Typography></TableCell>
                    <TableCell>
                      <Chip label={inv.status || 'pending'} size="small"
                        color={getStatusColor(inv.status)}
                        sx={{ textTransform:'capitalize', fontWeight:'medium' }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(inv.issueDate || inv.issue_date || inv.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={inv.status === 'overdue' ? 'error' : 'text.primary'}>
                        {formatDate(inv.dueDate || inv.due_date) || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display:'flex', gap:0.5 }}>
                        <Tooltip title="View">
                          <IconButton size="small" color="primary"
                            onClick={() => { setSelectedInvoice(inv); setViewDialog(true); }}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download PDF">
                          <IconButton size="small" color="secondary"
                            onClick={() => handleDownload(inv)}>
                            <PictureAsPdf />
                          </IconButton>
                        </Tooltip>
                        {['pending','overdue'].includes(inv.status) && (
                          <Tooltip title="Pay Now">
                            <IconButton size="small" color="success"
                              onClick={() => handlePayNow(inv)}>
                              <Payment />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p: 2, borderTop: 1, borderColor:'divider' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredInvoices.length} of {invoices.length} invoices
          </Typography>
        </Box>
      </Paper>

      {/* ── Payment Confirmation Dialog ── */}
      <Dialog open={payConfirmDialog} onClose={() => setPayConfirmDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Payment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to make a payment for invoice <strong>{invoiceToPay ? getNum(invoiceToPay) : ''}</strong>.
            <br /><br />
            Amount due: <strong>{invoiceToPay ? formatCurrency(getAmt(invoiceToPay)) : ''}</strong>
            <br /><br />
            You will be asked to upload your payment advice copy (bank receipt/screenshot) on the next page.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayConfirmDialog(false)}>Cancel</Button>
          <Button variant="contained" color="success" startIcon={<Payment />} onClick={handleConfirmPay}>
            Proceed to Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="sm" fullWidth>
        {selectedInvoice && (() => {
          const id  = getId(selectedInvoice);
          const num = getNum(selectedInvoice);
          const amt = getAmt(selectedInvoice);
          return (
            <>
              <DialogTitle>
                <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <Typography variant="h6">{num}</Typography>
                  <Chip label={selectedInvoice.status || 'pending'}
                    color={getStatusColor(selectedInvoice.status)}
                    sx={{ textTransform:'capitalize' }} />
                </Box>
              </DialogTitle>
              <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  {[
                    ['Invoice #',     num],
                    ['Enquiry ID',    `#${selectedInvoice.enquiry_id || id}`],
                    ['Product',       selectedInvoice.product || selectedInvoice.productName],
                    ['Quantity',      selectedInvoice.quantity ? `${selectedInvoice.quantity} containers` : null],
                    ['Issue Date',    formatDate(selectedInvoice.issueDate || selectedInvoice.issue_date)],
                    ['Due Date',      formatDate(selectedInvoice.dueDate || selectedInvoice.due_date)],
                    ['Payment Terms', selectedInvoice.paymentTerms || 'Net 7 days'],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <Grid item xs={6} key={label}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight="medium">{value}</Typography>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display:'flex', justifyContent:'space-between' }}>
                      <Typography variant="h6">Total:</Typography>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        {formatCurrency(amt)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setViewDialog(false)}>Close</Button>
                <Button variant="outlined" startIcon={<Download />}
                  onClick={() => handleDownload(selectedInvoice)}>
                  Download PDF
                </Button>
                {['pending','overdue'].includes(selectedInvoice.status) && (
                  <Button variant="contained" color="success" startIcon={<Payment />}
                    onClick={() => { setViewDialog(false); handlePayNow(selectedInvoice); }}>
                    Pay Now — {formatCurrency(amt)}
                  </Button>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Container>
  );
};

export default BuyerInvoices;